import { Button, StatusDot } from "@rv/aura-web";
import "../styles/ResponseComponent.css";
import { Refresh, DataImport, NavigationRight } from "@rv/aura-icons";
import { ResponseData } from "../types/ReponseData";
import { useContext } from "react";
import { StateContext } from "../context/StateContext";
import { ActivateButtonWrapper, PanelContent } from "./ContentsStyled";
import labels from "../translation/index.json";
import { RequestParameters } from "../types/RequestParameters";

interface ResponseProps {
  responseData: ResponseData;
}

export default function Response({ responseData }: ResponseProps) {
  const { serverResponse, responseType } = responseData;

  const isActivateMode = responseType === "activation";
  const status = serverResponse.status;
  const appState = useContext(StateContext);

  const getMessage = (): string => {
    switch (serverResponse.status) {
      case "success":
        if (!serverResponse.messages) {
          if (isActivateMode) {
            return `Your license was successfully activated.`;
          } else {
            return "Your license was successfully deactivated. You can now close this window or process another license.";
          }
        } else {
          return serverResponse.messages[0];
        }

      case "error":
      case "permission":
        return labels.errorMessage;

      case "input":
        return `An error occurred: ${
          serverResponse.messages ? serverResponse.messages[0] : "Unknown Error"
        }`;
      default:
        return "";
    }
  };

  const getStatusDotType = (
    status: string,
  ): "good" | "warning" | "error" | "unknown" => {
    switch (status) {
      case "success":
        return "good";
      case "error":
      case "permission":
      case "input":
        return "error";

      default:
        return "unknown";
    }
  };

  function getFormattedDate(): string {
    const date = new Date();
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");

    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds()).padStart(2, "0");

    return `${year}-${month}-${day}-${hours}.${minutes}.${seconds}`;
  }

  function generateAndPromptDownload() {
    var element = document.createElement("a");
    var date = getFormattedDate();
    const requestParams = appState.currentState.requestParameters as RequestParameters;

    element.setAttribute(
      "href",
      "data:text/plain;charset=utf-8," +
        encodeURIComponent(JSON.stringify(serverResponse.object)),
    );
    const action = requestParams.requestType === "activation"? "Activation" : "Deactivation";
    const productName = requestParams.productName;
    if(productName === "Unknown") {
      element.setAttribute("download", `${requestParams.productKey}_${action}-Response_${date}.json`);
    } else {
      element.setAttribute("download", `${productName}_${requestParams.productKey}_${action}-Response_${date}.json`);
    }

    element.style.display = "none";
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  }

  function startOver() {
    appState.updateState({
      requestParameters: undefined,
      status: "provide-file",
    });
  }

  const retry = (): void => {
    appState.updateState({
      ...appState.currentState,
      status: "request-in-progress",
    });
  };

  return (
    <>
      <PanelContent
        height={`${(status === "success" && !isActivateMode) || serverResponse.status === "input" ? 230 : 200}px`}
      >
        <StatusDot
          type={getStatusDotType(status)}
          className="final-status-logo"
        />
        <p className="text-message">{getMessage()}</p>
      </PanelContent>
      <ActivateButtonWrapper>
        {status === "success" && isActivateMode && (
          <Button
            className="shift-icon-right"
            label={"Download License File"}
            Icon={DataImport}
            variant="iconLeft"
            onClick={generateAndPromptDownload}
          />
        )}

        {status !== "success" && (
          <Button
            className="shift-icon-right"
            label={labels.tryAgain}
            Icon={Refresh}
            variant="iconLeft"
            onClick={retry}
          />
        )}

        <Button
          className="shift-icon-right"
          label={"Activate or Deactivate Another License"}
          Icon={NavigationRight}
          variant="iconLeft"
          onClick={startOver}
        />
      </ActivateButtonWrapper>
    </>
  );
}
